import React, { ReactElement } from "react"
import { graphql, useStaticQuery } from "gatsby"
import { HelmetDatoCms } from "gatsby-source-datocms"
import { IntlProvider } from "react-intl"
import { ParallaxProvider } from "react-scroll-parallax"
import "focus-visible"
import { BreadcrumbsContext } from "../contexts/breadcrumbs"
import extractMessages from "../utils/extract-messages"
import PageHeader from "../components/page-header"
import Navigation from "./menu/navigation"
import MainHero from "./main-hero"
import CookieConsentModal from "./cookie-consent-modal"
import LinkToTop from "./sticky-buttons/link-to-top"
import AlertBar from "../components/alert-bar"
import Footer from "./footer"
import "../assets/scss/site.scss"

export const query = graphql`
  fragment PageFields on DatoCmsPage {
    slug
    title
    sectionTitle
    headline
    headlineNode {
      childMarkdownRemark {
        html
      }
    }
    headerImage {
      gatsbyImageData(
        imgixParams: { fit: "crop", crop: "focalpoint", w: "1600", h: "700" }
      )
      url_xl: url(imgixParams: { w: "1900", q: 90 })
      focalPoint {
        x
        y
      }
    }
    headerVideo {
      video {
        high: mp4Url(res: high)
        medium: mp4Url(res: medium)
        low: mp4Url(res: low)
        streamingUrl
      }
    }
    useMainHero
    titleImage {
      url
    }
    preambleNode {
      childMarkdownRemark {
        html
      }
    }
    ctas {
      id
      text
      url
      isTransparent
      openInNewTab
      hasColor
    }
    seoMetaTags {
      ...GatsbyDatoCmsSeoMetaTags
    }
  }
`

interface PageProps extends Queries.PageFieldsFragment {
  children: ReactElement
  headerVariant?: "has-title" | "dark" | "o-row"
  metaDescription?: string
  alertBar?: Queries.AlertBarFieldsFragment
  hideNavigation?: boolean | null
  forceBlackTransparentNavigation?: boolean | null
  pathName?: string
}

// Note: Keep these in sync with typography.scss.
const preloadFonts = [
  ["NorthvoltGritPro-Regular.woff2", "woff2"],
  ["NorthvoltGritPro-Medium.woff2", "woff2"],
  ["NorthvoltGritPro-SemiBold.woff2", "woff2"],
  ["NorthvoltGritPro-Bold.woff2", "woff2"],
]

const Page = (props: PageProps) => {
  const data = useStaticQuery(graphql`
    query StaticPageFields {
      topNavigation: datoCmsTopNavigation {
        ...TopNavigationFields
      }
      translations: allLanguagesYaml {
        ...LanguagesData
      }
      footer: datoCmsFooter {
        ...FooterFields
      }
      breadcrumbsData: allDatoCmsBreadcrumbsItem {
        nodes {
          ...BreadcrumbsItemFields
        }
      }
    }
  `)

  const usePageHeader =
    !props.useMainHero &&
    (!!props.headerImage || (props.headerVideo && props.headerVideo.video))

  return (
    <>
      <HelmetDatoCms seo={props.seoMetaTags}>
        {preloadFonts.map(([filename, format], i) => (
          <link
            key={i}
            rel="preload"
            as="font"
            href={`/fonts/${filename}`}
            type={`font/${format}`}
            crossOrigin="anonymous"
          />
        ))}
        {/* Work around missing description in seoMetaTags. */}
        {props.metaDescription && (
          <meta name="description" content={props.metaDescription} />
        )}
        {props.metaDescription && (
          <meta name="og:description" content={props.metaDescription} />
        )}
        {props.metaDescription && (
          <meta name="twitter:description" content={props.metaDescription} />
        )}
        <meta
          name="google-site-verification"
          content="jOXxu-nsBdFjbOw46TecJk4skzzG5-JrUcyyfmBDIrE"
        />
      </HelmetDatoCms>
      <IntlProvider
        messages={extractMessages(data.translations, "en")}
        locale="en"
        defaultLocale="en"
      >
        {props.alertBar && <AlertBar {...props.alertBar} />}
        <BreadcrumbsContext.Provider value={data.breadcrumbsData.nodes}>
          <Navigation
            {...data.topNavigation}
            pathName={props.pathName}
            hideNavigation={!!props.hideNavigation}
            forceBlackTransparentNavigation={
              props.forceBlackTransparentNavigation
            }
            isTransparent={
              !props.forceBlackTransparentNavigation &&
              (props.useMainHero || usePageHeader)
            }
          />
        </BreadcrumbsContext.Provider>
        <LinkToTop />
        {props.useMainHero && (
          <MainHero
            title={props.title}
            headerImage={props.headerImage && props.headerImage}
            headerVideo={props.headerVideo && props.headerVideo.video}
            titleImage={props.titleImage && props.titleImage.url}
            headline={
              props.headlineNode &&
              props.headlineNode.childMarkdownRemark &&
              props.headlineNode.childMarkdownRemark.html
            }
            preamble={
              props.preambleNode && props.preambleNode.childMarkdownRemark.html
            }
            ctas={props.ctas}
            variant={props.headerVariant}
          />
        )}
        {usePageHeader && (
          <PageHeader
            title={props.title}
            sectionTitle={props.sectionTitle}
            headerImage={props.headerImage}
            headerVideo={props.headerVideo && props.headerVideo.video}
            variant={props.headerVariant}
          />
        )}
        <ParallaxProvider>{props.children}</ParallaxProvider>
        <Footer hideNavigation={props.hideNavigation} {...data.footer} />
        <CookieConsentModal />
      </IntlProvider>
    </>
  )
}

export default Page
